import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import "./CallReviewSheetPopup.css";
import { notify } from "../../Helper/notification";
import { downloadCallReviewPDF } from "./generateCallReviewPDF";
import RichTextEditor from "./RichTextEditor";
import {
  getCallReview,
  getCallExecutiveSummary,
  getCallReviewFeedback,
} from "../../../operations/services/getCallReview";

/* ─── METADATA HELPERS ─── */

// Mirrors the reviewer-name lookup pattern used in AgentFeedbackModal.jsx's
// getSenderInfo() so we have a sensible default when the parent doesn't pass
// a reviewerName prop explicitly.
// Whether reviewers may mark a criterion "N/A" (excluded from scoring rather
// than counted as 0). Gated on an env var so it can ship dark: set in .env.dev,
// absent from .env.prod, so the option is offered in dev and hidden in
// production until the feature is signed off. Read-side handling of existing
// "NA" values is unconditional, so nothing already saved is affected.
const isNaScoringEnabled = Boolean(process.env.REACT_APP_ENABLE_NA_SCORING);

const getReviewerNameFromStorage = () => {
  try {
    const loginState = localStorage.getItem("loginState");
    if (!loginState) return "";
    const userData = JSON.parse(loginState)?.data?.[0];
    if (!userData) return "";
    return `${userData.FirstName || ""} ${userData.LastName || ""}`
      .replace(/\bnull\b/gi, "")
      .trim();
  } catch {
    return "";
  }
};

const formatToday = () =>
  new Date().toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

// Formats the backend's `updatedAt` (ISO UTC) into US Eastern time to match
// "02/24/2026, 09:13:59 AM EST" — the save timestamp shown as the PDF's
// Review Date.
// Exported so the Call Review section on the details page can render the same
// audit line as this modal's header. Two copies of this formatting would drift,
// and the two are read side by side — the page behind the modal shows one, the
// header shows the other.
export const formatReviewDateTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
};

/* ─── FIELD LABELS ─── */

const USING_TOOLS_FIELDS = [
  { label: "Did the AC use the intro script?" },
  { label: "Did the AC attempt the 4 W's - What's going on?" },
  { label: "Did the AC attempt the 4 W's - How long has it been going on for?" },
  { label: "Did the AC attempt the 4 W's - Was there something that happened to start all this?" },
  { label: "Did the AC attempt the 4 W's - Why now?" },
  { label: "Was the AC in control of the call?" },
  { label: "Did AC ask for the referral source – How did you hear about us?", options: [0, 2] },
];

const SALES_FIELDS = [
  { label: "Did the AC do a good job of understanding wants and needs?" },
  { label: "Did the AC individualize a pitch on how Monte Nido's program can help with the client's wants and needs?" },
  { label: "Did the AC show empathy to the caller's situation?" },
];

const OBJECTIONS_FIELDS = [
  { label: "Did the AC initiate the assessment process on the call?" },
  { label: "Did the AC explain insurance without creating a barrier to treatment?" },
  { label: "Did the AC do a good job of overcoming objections?" },
  { label: "Did the AC create a sense of urgency?" },
  { label: "Did the AC schedule next steps?" },
];

/* ─── DEFAULT DATA ─── */

const DEFAULT_DATA = {
  usingTools: [0, 0, 0, 0, 0, 0, 0],
  sales: [0, 0, 0],
  objections: [0, 0, 0, 0, 0],
};

const DEFAULT_FEEDBACK = {
  whatWentWell: "",
  whatCanImprove: "",
  generalComments: "",
};

/* ─── RAW-POINTS TOTALS (N/A-aware) ───
 * The scorecard's "points" model (Section Total / Overall Score, and the
 * popup's Download PDF which mirrors them) is a simple max-face-value count —
 * NOT the weighted model used by the on-screen summary/average. Each question's
 * max is the top of its options (default 1; the referral question is 2). A
 * question marked "NA" is EXCLUDED, so the "out of" total shrinks to match
 * (e.g. one of five objections marked N/A makes the section total 4, not 5). */
const questionMax = (field) => {
  const opts = field?.options || [0, 1];
  return Math.max(...opts);
};
const sectionMaxExclNA = (fields = [], values = []) =>
  fields.reduce(
    (sum, field, i) => sum + (values[i] === "NA" ? 0 : questionMax(field)),
    0,
  );

/* ─── SECTION BLOCK ─── */

const SectionBlock = ({
  title,
  fields,
  values,
  sectionKey,
  sectionTotal,
  onFieldChange,
}) => {
  const earned = values.reduce((sum, value) => {
  return sum + (value === "NA" ? 0 : Number(value) || 0);
}, 0);

  return (
    <div className="crs-block">
      <h3 className="crs-block-title">{title}</h3>

      <table className="crs-table">
        <thead>
          <tr>
            <th className="crs-th crs-th-label">CRITERIA</th>
            <th className="crs-th crs-th-pts">POINTS</th>
          </tr>
        </thead>

        <tbody>
          {fields.map((field, idx) => (
            <tr key={idx}>
              <td className="crs-td crs-td-label">{field.label}</td>
              <td className="crs-td crs-td-pts">
                <select
                  className="crs-pts-select"
                  value={values[idx] ?? 0}
                  onChange={(e) => {
                    const v = e.target.value;
                    onFieldChange(
                      sectionKey,
                      idx,
                      v === "NA" ? "NA" : Number(v),
                    );
                  }}
                >
                  {(field.options || [0, 1]).map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                  {/* N/A scoring is gated per environment. This is the only
                      control that can create an "NA" value; everything that
                      READS one still works, so a sheet already saved with N/A
                      keeps rendering and scoring correctly wherever it's
                      hidden. Enable by setting REACT_APP_ENABLE_NA_SCORING. */}
                  {(isNaScoringEnabled || values[idx] === "NA") && (
                    <option value="NA">N/A</option>
                  )}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

     <p className="crs-section-total">
  {sectionTotal === 0 ? (
    // Whole section marked N/A — nothing to score (matches the PDF).
    "Section Total – N/A"
  ) : (
    <>
      Section Total – {sectionTotal} points
      <span style={{ fontWeight: 400 }}> (Earned: {earned})</span>
    </>
  )}
</p>
    </div>
  );
};

/* ─── MAIN POPUP ─── */

const CallReviewSheetPopup = ({
  isOpen,
  onClose,
  initialData,
  onSave,
  isSaving = false,
  // Metadata used to build the PDF / CSV / email summary. All optional —
  // when a backend isn't wired up yet for a given call, these simply fall
  // back to sensible client-side defaults below.
  callId,
  vonageCallId,
  magicHealthId,
  agentName,
  memberName,
  reviewerName,
  reviewDate,
  // Raw decrypted GET response (sections/totals/average/weightage/feedback)
  // for the call — Download reads straight from this, no backend call.
  reviewData,
  // Optional override for the "Email to Agent" recipient.
  agentEmail,
  // Dock the sheet against the right edge and leave the page's left panel —
  // agent details, audio player, transcript — fully usable underneath. A
  // reviewer writes notes WHILE listening; a centred modal with a full-screen
  // backdrop made that impossible, since the player was visible but dead.
  dockRight = false,
  // When true, the scoring sections are hidden (call categories with no CRS
  // scoring). Only the feedback fields + Download PDF / Email to Agent remain;
  // the PDF shows an Executive Summary in place of the Score Sheet and the
  // email omits the Score Sheet. The agent email is resolved server-side.
  feedbackOnly = false,
}) => {
  const [formData, setFormData] = useState(DEFAULT_DATA);
  const [feedbackData, setFeedbackData] = useState(DEFAULT_FEEDBACK);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // Rendered preview of the outgoing email: { subject, html, to, cc }.
  // `editedBody` holds the reviewer's edits to it — null means untouched, so
  // an unedited send uses the freshly generated message rather than a copy
  // that could have gone stale.
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [editedBody, setEditedBody] = useState(null);
  const previewBodyRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);

  // Who last saved this review, and when. Scoring calls carry it on the
  // review payload; feedback-only calls have their own record and are fetched
  // separately, so both paths funnel into this one piece of state and the
  // header doesn't have to know which kind of call it is showing.
  const [audit, setAudit] = useState({ name: "", at: null });
  const lastUpdatedByName = audit.name;
  const lastUpdatedAt = audit.at;

  // Capture initialData in a ref so we can read its latest value inside the
  // effect without making it a dependency (avoids re-init on every render).
  const initialDataRef = useRef(initialData);
  useEffect(() => {
    initialDataRef.current = initialData;
  });

  // Initialize form state ONCE each time the popup transitions to open.
  // Intentionally omit initialData from deps — the parent creates a new object
  // reference on every render, which would cause this effect to fire and reset
  // user edits mid-session. Score edits and feedback-text edits share this
  // same guard, so editing one never resets the other while the popup is open.
  useEffect(() => {
    if (isOpen) {
      const data = initialDataRef.current;
      // Merge onto DEFAULT_DATA so the scoring arrays always exist — the
      // feedback-only modal passes only feedback text (no scores), and
      // totalEarned/reduces below must never see an undefined array.
      setFormData({ ...DEFAULT_DATA, ...(data || {}) });
      setFeedbackData({
        whatWentWell: data?.whatWentWell || "",
        whatCanImprove: data?.whatCanImprove || "",
        generalComments: data?.generalComments || "",
      });
      setShowShareModal(false);
      setIsEmailing(false);
      setIsDownloading(false);
      setIsDirty(false);
      setShowDiscardModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Load the audit line. Scoring calls already have it on reviewData; a
  // feedback-only call keeps its own record, so that one is fetched. Failing
  // to load it hides the line rather than blocking the modal — not knowing
  // who last edited a review is no reason to prevent editing it.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;

    if (!feedbackOnly) {
      setAudit({
        name: reviewData?.updatedByName || "",
        at: reviewData?.updatedAt || null,
      });
      return undefined;
    }

    setAudit({ name: "", at: null });
    (async () => {
      try {
        const latest = await getCallReviewFeedback(callId);
        if (cancelled) return;
        setAudit({
          name: latest?.updatedByName || "",
          at: latest?.updatedAt || null,
        });
      } catch (err) {
        console.error("[CallReviewSheetPopup] audit fetch failed:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, feedbackOnly, callId, reviewData]);

  /* ✅ Prevent scroll */
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  /* "Docked" no longer moves the sheet: it is the ordinary centred modal over
     the ordinary dimmed backdrop. What changes is the page underneath — while
     the sheet is open, a body class lifts the call-details left panel above
     the backdrop (see DetailsScreen.css), so the audio player and transcript
     stay visible and clickable through the dim. The class lives on <body>
     because the panel is not a descendant of this portal. */
  useEffect(() => {
    if (!isOpen || !dockRight) return undefined;
    document.body.classList.add("crs-sheet-docked");
    // The page scroller is frozen while docked (DetailsScreen.css). If it was
    // already scrolled so the panel's top is off screen, bring it back once,
    // now, rather than leaving the reviewer with a panel they can't reach.
    const scroller = document.querySelector(".details-page-main");
    const panel = scroller && scroller.querySelector(".contact-details");
    if (scroller && panel) {
      const top = panel.getBoundingClientRect().top;
      if (top < 16) scroller.scrollTop += top - 16;
    }
    return () => {
      document.body.classList.remove("crs-sheet-docked");
      document.body.style.removeProperty("--crs-panel-max-h");
    };
  }, [isOpen, dockRight]);

  /* The sheet sits beside that panel, not on top of it: centred in the space
     to its right and aligned to its top and bottom edges, so the two read as
     side-by-side cards. The panel is a grid column of `minmax(280px, 24vw)`
     whose element is re-created when the navigator moves to another call, so
     it is looked up and measured every frame while the sheet is open — one
     querySelector and one getBoundingClientRect, far cheaper than the event
     bookkeeping (resize, scroll, ResizeObserver, remount) it replaces, and
     immune to the layout shifts that bookkeeping missed. State only changes
     when the numbers do.

     The box is clamped to the viewport: a panel scrolled partly out of view
     must not produce a negative offset (CSS drops negative padding outright,
     which is how the sheet once ended up under the panel). If the panel is
     missing, too small, or leaves too little room beside it, the rect is null
     and the sheet falls back to the ordinary centred modal. */
  const [dockRect, setDockRect] = useState(null);
  useEffect(() => {
    if (!isOpen || !dockRight) return undefined;
    let frame = 0;
    let last = "";
    let lastMaxH = "";
    const tick = () => {
      const panel = document.querySelector(".details-page-main .contact-details");
      let next = null;
      if (panel) {
        const r = panel.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const left = Math.round(r.right + 20);
        const top = Math.max(Math.round(r.top), 16);
        const bottom = Math.min(Math.round(r.bottom), vh - 16);
        const height = bottom - top;
        if (r.width > 0 && height >= 360 && vw - left >= 480) {
          next = { left, top, height };
        }
        // The panel's grid row has a 620px floor, so on a short viewport the
        // panel runs off the bottom — and with the page scroller frozen its
        // lower part would be unreachable. Cap it to what fits so its own
        // scrollbar covers everything; the sheet then aligns to the same
        // capped box. Set on <body> because the panel is outside this portal.
        const maxH = `${Math.max(320, vh - top - 16)}px`;
        if (maxH !== lastMaxH) {
          lastMaxH = maxH;
          document.body.style.setProperty("--crs-panel-max-h", maxH);
        }
      }
      const key = next ? `${next.left},${next.top},${next.height}` : "";
      if (key !== last) {
        last = key;
        setDockRect(next);
      }
      frame = window.requestAnimationFrame(tick);
    };
    tick();
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen, dockRight]);

  /* ✅ Calculate total */
  const totalEarned = useMemo(() => {
    const sum = (arr) =>
      (Array.isArray(arr) ? arr : []).reduce(
        (a, b) => a + (b === "NA" ? 0 : Number(b) || 0),
        0,
      );
    return (
      sum(formData.usingTools) +
      sum(formData.sales) +
      sum(formData.objections)
    );
  }, [formData]);

  /* Grant editing to the reviewer's own feedback, and nothing else.
   *
   * The server marks those blocks with `data-crs-edit`. Everything around them
   * — the greeting, the Call Details table (Convo ID, Vonage ID, date, caller
   * name, summary), the Score Sheet, the section headings and the sign-off —
   * describes what happened on the call. Letting it be retyped in a compose box
   * would mean the agent receives figures that no longer match the review.
   *
   * Runs after the markup is injected, and again whenever a new preview
   * replaces it, since that rewrites the whole subtree. It has to sit up here
   * with the other hooks: there is an `if (!isOpen) return null` below, and a
   * hook after it would run conditionally. */
  useEffect(() => {
    const root = previewBodyRef.current;
    if (!root || !emailPreview) return;
    root.querySelectorAll("[data-crs-edit]").forEach((el, i) => {
      el.setAttribute("contenteditable", "true");
      el.setAttribute("role", "textbox");
      el.setAttribute("aria-multiline", "true");
      el.setAttribute("aria-label", `Editable feedback section ${i + 1}`);
    });
  }, [emailPreview]);

  /* Max points per section and overall, EXCLUDING questions marked N/A. Drives
     the "Section Total"/"Overall Score" out-of values both on screen and in the
     PDF (via buildExportData), so an N/A question shrinks the denominator. */
  const utTotal = sectionMaxExclNA(USING_TOOLS_FIELDS, formData.usingTools);
  const sTotal = sectionMaxExclNA(SALES_FIELDS, formData.sales);
  const oTotal = sectionMaxExclNA(OBJECTIONS_FIELDS, formData.objections);
  const overallMax = utTotal + sTotal + oTotal;

  if (!isOpen) return null;

  /* 🔄 Update values */
  const handleSectionChange = (sectionKey, idx, val) => {
    setIsDirty(true);
    setFormData((prev) => {
      const updated = [...prev[sectionKey]];
      updated[idx] = val;
      return { ...prev, [sectionKey]: updated };
    });
  };

  const handleFeedbackChange = (field, value) => {
    setIsDirty(true);
    setFeedbackData((prev) => ({ ...prev, [field]: value }));
  };

  /* 🚪 Safe close — prompts when unsaved changes exist */
  const handleClose = () => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardModal(false);
    onClose();
  };

  /* 💾 Persist scores + feedback text via the parent's onSave handler.
     `options.sendEmail`/`options.agentEmail` are forwarded straight through
     to the same encrypted POST — used by the "Email to Agent" action. */
  const persistReview = async (options) => {
    if (!onSave) return undefined;
    // Skip the save API call when nothing was edited. The exceptions are the
    // actions that must reach the backend regardless: sending the email, and
    // rendering the preview (which writes nothing).
    if (!isDirty && !options?.sendEmail && !options?.previewOnly)
      return undefined;
    const payload = {
      ...formData,
      overallScore: totalEarned,
      whatWentWell: feedbackData.whatWentWell,
      whatCanImprove: feedbackData.whatCanImprove,
      generalComments: feedbackData.generalComments,
    };
    // Whether this save carries an actual edit. "Email to Agent" reaches the
    // backend even with nothing changed, and without this the act of emailing
    // or downloading would record the sender as the last reviewer — making
    // "last reviewed" mean "last opened".
    return onSave(payload, { ...options, contentChanged: isDirty });
  };

  /* Section-level max/achieved/outOf5/percentage already computed server-side
     in the GET response (reviewData.sections.UT/S/O). Falls back to a simple
     client-side achieved total when that data isn't available yet. */
  const sectionSummary = (key, values, sectionTotal) => {
    const meta = reviewData?.sections?.[key];
    if (meta) {
      return {
        max: meta.max,
        achieved: meta.achieved,
        outOf5: meta.outOf5,
        percentage: meta.percentage,
      };
    }
    return {
      max: sectionTotal,
      achieved: (values || []).reduce(
        (s, v) => s + (v === "NA" ? 0 : Number(v) || 0),
        0,
      ),
    };
  };

  /* Export data is built entirely from local state + the already-fetched
     `reviewData` (the decrypted GET response) — no backend call. Shared by
     both the PDF and CSV exports. */
  const buildExportData = (savedReviewDate) => ({
    callId,
    vonageCallId,
    magicHealthId,
    agentName,
    memberName,
    // The reviewer and review date describe who last actually reviewed this
    // call — not whoever happens to be exporting it. Downloading a PDF or
    // emailing it is not reviewing, so neither stamps a name or a date.
    //
    // Only the person making an edit becomes the reviewer, and only then does
    // the date move. With no edit and nothing previously recorded, both are
    // blank and the PDF omits those rows entirely: an unreviewed call should
    // look unreviewed rather than credit whoever opened it.
    reviewDate: isDirty
      ? savedReviewDate || formatToday()
      : formatReviewDateTime(lastUpdatedAt) || "",
    reviewerName: isDirty
      ? reviewerName || getReviewerNameFromStorage()
      : lastUpdatedByName || "",
    overallScore: totalEarned,
    maxScore: overallMax,
    overallAverage: reviewData?.average,
    sections: [
      {
        title: "Using the Tools",
        fields: USING_TOOLS_FIELDS,
        values: formData.usingTools,
        sectionTotal: utTotal,
        summary: sectionSummary("UT", formData.usingTools, utTotal),
      },
      {
        title: "Sales",
        fields: SALES_FIELDS,
        values: formData.sales,
        sectionTotal: sTotal,
        summary: sectionSummary("S", formData.sales, sTotal),
      },
      {
        title: "Objections",
        fields: OBJECTIONS_FIELDS,
        values: formData.objections,
        sectionTotal: oTotal,
        summary: sectionSummary("O", formData.objections, oTotal),
      },
    ],
    whatWentWell: feedbackData.whatWentWell,
    whatCanImprove: feedbackData.whatCanImprove,
    generalComments: feedbackData.generalComments,
  });

  /* Save button opens the Save & Share confirmation instead of saving immediately */
  const handleSaveClick = () => setShowShareModal(true);

  // When there's nothing to save, `persistReview` skips the API call and the
  // parent's onSave never fires — which is normally what closes the popup —
  // so close it here explicitly rather than relying on that side effect.
  const handleNotNow = async () => {
    setShowShareModal(false);
    await persistReview();
    onClose();
  };

  // Saves first (same encrypted POST as "Not Now" — no-op when nothing was
  // edited), then re-fetches the Call Review via the same GET the panel used
  // on load so the PDF's Review Date always reflects the latest persisted
  // `updatedAt` from the database — whether that's from a save made just now
  // or the last time this review was actually saved. Only falls back to the
  // popup's original `reviewData`/today when that re-fetch fails.
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      // Non-scoring calls: persist the feedback (so it reopens in edit mode),
      // then pull the executive summary and render the PDF (no Score Sheet).
      if (feedbackOnly) {
        const saveResponse = await persistReview();
        let savedReviewDate = formatReviewDateTime(
          saveResponse?.updatedAt ||
            saveResponse?.UpdatedAt ||
            saveResponse?.reviewedAt,
        );
        if (!savedReviewDate) {
          try {
            const latest = await getCallReviewFeedback(callId);
            savedReviewDate = formatReviewDateTime(latest?.updatedAt);
          } catch (fetchErr) {
            console.error(
              "[CallReviewSheetPopup] failed to re-fetch feedback before PDF:",
              fetchErr,
            );
          }
        }
        let executiveSummary = "";
        try {
          const s = await getCallExecutiveSummary(callId);
          const raw = s?.executiveSummary;
          executiveSummary =
            typeof raw === "string" ? raw : raw ? JSON.stringify(raw) : "";
        } catch (sErr) {
          console.error("[CallReviewSheetPopup] executive summary fetch failed:", sErr);
        }
        await downloadCallReviewPDF({
          ...buildExportData(savedReviewDate),
          feedbackOnly: true,
          executiveSummary,
        });
        notify.success("PDF downloaded successfully");
        return;
      }

      await persistReview();
      let savedReviewDate = "";
      try {
        const latest = await getCallReview(callId);
        savedReviewDate = formatReviewDateTime(latest?.updatedAt);
      } catch (fetchErr) {
        console.error("[CallReviewSheetPopup] failed to re-fetch call review before PDF:", fetchErr);
      }
      await downloadCallReviewPDF(buildExportData(savedReviewDate));
      notify.success("PDF downloaded successfully");
    } catch (err) {
      notify.error("Failed to generate the PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
    setShowShareModal(false);
    onClose();
  };

  /* "Email to Agent" opens the preview rather than sending straight away.
     The reviewer sees exactly what the agent will receive, and can correct it,
     before anything leaves — which is the point of a preview. Sending happens
     from that dialog. */
  const handleEmailToAgent = async () => {
    // No client-side recipient gate. The backend resolves recipients itself —
    // chosen recipients, else EVERY handling agent on the call, else the
    // client-matched agentEmail as a last resort — and the preview fails with
    // a clear message when nothing resolves. Requiring agentEmail here
    // disabled "Email to Agent" on every multi-agent call: the page passes
    // the agents as one joined string ("Kerrianne Larson, Jamie Hermans"),
    // which can never name-match a single directory entry.
    setIsPreviewing(true);
    try {
      const preview = await persistReview({ previewOnly: true, agentEmail });
      if (!preview?.html) {
        notify.error("Couldn't build the preview. Please try again.");
        return;
      }
      setEmailPreview(preview);
      setEditedBody(null); // start from the freshly rendered message
    } catch (err) {
      console.error("[CallReviewSheetPopup] email preview failed:", err);
      notify.error("Couldn't build the preview. Please try again.");
    } finally {
      setIsPreviewing(false);
    }
  };

  /* Capture the edited message, without the editing scaffolding.
   *
   * The attributes the effect above adds at runtime are how the preview works,
   * not part of the message, so they come back out. `data-crs-edit` is left
   * alone deliberately: the server puts it there, so it is already present in
   * the message when nothing was edited. Stripping it only on the edited path
   * would make the two produce different markup for no reason. */
  const captureEditedBody = () => {
    const root = previewBodyRef.current;
    if (!root) return;
    setEditedBody(
      root.innerHTML
        .replace(/\s*contenteditable\s*=\s*(["'])[^"']*\1/gi, "")
        .replace(/\s*(role|aria-multiline|aria-label)\s*=\s*(["'])[^"']*\2/gi, ""),
    );
  };

  /* Actually send, from the preview. Any edit made in the preview is sent
     instead of the generated body. */
  const handleConfirmSend = async () => {
    setIsEmailing(true);
    try {
      await persistReview({
        sendEmail: true,
        agentEmail,
        ...(editedBody !== null ? { emailHtmlOverride: editedBody } : {}),
      });
    } finally {
      setIsEmailing(false);
    }
    setEmailPreview(null);
    setEditedBody(null);
    setShowShareModal(false);
  };

  const isShareActionBusy =
    isEmailing || isDownloading || isSaving || isPreviewing;

  // Docked: the left panel is live (lifted above the backdrop by the body class
  // set above) and the sheet is placed beside it via the measured box, carried
  // as CSS variables on the wrapper so every sibling overlay (sheet, share
  // prompt, preview, discard) inherits them. Backdrop clicks must not close
  // the sheet. The wrapper has no layout of its own.
  const docked = Boolean(dockRight);
  const placed = docked && dockRect != null;
  const portalStyle = placed
    ? {
        "--crs-dock-left": `${dockRect.left}px`,
        "--crs-dock-top": `${dockRect.top}px`,
        "--crs-dock-height": `${dockRect.height}px`,
      }
    : undefined;
  const portalClass = [
    "crs-portal-root",
    docked ? "crs-portal-root--docked" : "",
    placed ? "crs-portal-root--placed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return ReactDOM.createPortal(
    <div className={portalClass} style={portalStyle}>
      <div
        className={`feedback-overlay${docked ? " feedback-overlay--docked" : ""}`}
        onClick={(e) =>
          // Clicking the backdrop closes a centred modal. Docked, the backdrop
          // is a working surface next to a live player, and a stray click must
          // not throw the reviewer's notes into a discard prompt.
          !docked &&
          e.target === e.currentTarget &&
          !showShareModal &&
          handleClose()
        }
      >
        <div
          className={`crs-popup-content${docked ? " crs-popup-content--docked" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* HEADER */}
          <div className="crs-popup-header">
            <h2 className="crs-main-title">
              {feedbackOnly ? "Reviewer Feedback" : "Call Review Sheet"}
            </h2>

            {/* Who last touched this review and when. Sits in the header so an
                operations manager can see it without scrolling or opening
                anything — the point is to know at a glance whether a review is
                current and whose activity to follow up on.

                The name is shown, never the stored email: "Updated By:
                jane.doe@montenido.com" reads badly next to a person's own
                work. The time is Eastern, matching the review dates used
                everywhere else in this modal and on the PDF. */}
            {(lastUpdatedByName || lastUpdatedAt) && (
              <div className="crs-audit-line">
                {lastUpdatedByName && (
                  <span className="crs-audit-item">
                    <strong>
                      {feedbackOnly ? "Reviewed By:" : "Updated By:"}
                    </strong>{" "}
                    {lastUpdatedByName}
                  </span>
                )}
                {lastUpdatedAt && (
                  <span className="crs-audit-item">
                    <strong>Last Updated:</strong>{" "}
                    {formatReviewDateTime(lastUpdatedAt)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* BODY */}
          <div className="crs-body">
            {!feedbackOnly && (
              <>
            <SectionBlock
              title="Using the Tools"
              fields={USING_TOOLS_FIELDS}
              values={formData.usingTools}
              sectionKey="usingTools"
              sectionTotal={utTotal}
              onFieldChange={handleSectionChange}
            />

            <SectionBlock
              title="Sales"
              fields={SALES_FIELDS}
              values={formData.sales}
              sectionKey="sales"
              sectionTotal={sTotal}
              onFieldChange={handleSectionChange}
            />

            <SectionBlock
              title="Objections"
              fields={OBJECTIONS_FIELDS}
              values={formData.objections}
              sectionKey="objections"
              sectionTotal={oTotal}
              onFieldChange={handleSectionChange}
            />

            {/* TOTAL */}
            <div className="crs-overall-row">
              <span style={{ fontWeight: 700 }}>
                Overall Score:
              </span>
              <input
                type="text"
                className="crs-pts-select"
                style={{ width: "50px", textAlign: "center" }}
                value={totalEarned}
                readOnly
              />
              <span>/ {overallMax} points</span>
            </div>
            <p
              className="crs-score-disclaimer"
              style={{
                margin: "6px 0 0",
                fontSize: "12px",
                fontStyle: "italic",
                color: "#8a6d1a",
              }}
            >
              ! Note: The overall score is not calculated using weightage
              factors.
            </p>
              </>
            )}

            {/* FEEDBACK */}
            <div className="crs-block">
              <h3 className="crs-block-title">Reviewer Feedback</h3>

              <div className="crs-feedback-stack">
                <div className="crs-feedback-group">
                  <label className="crs-feedback-label" htmlFor="crs-went-well">
                    What Went Well
                  </label>
                  <RichTextEditor
                    id="crs-went-well"
                    placeholder="Highlight what the agent did well on this call..."
                    value={feedbackData.whatWentWell}
                    onChange={(v) => handleFeedbackChange("whatWentWell", v)}
                  />
                </div>

                <div className="crs-feedback-group">
                  <label className="crs-feedback-label" htmlFor="crs-can-improve">
                    What Can Improve
                  </label>
                  <RichTextEditor
                    id="crs-can-improve"
                    placeholder="Note areas the agent could improve on..."
                    value={feedbackData.whatCanImprove}
                    onChange={(v) => handleFeedbackChange("whatCanImprove", v)}
                  />
                </div>

                <div className="crs-feedback-group">
                  <label className="crs-feedback-label" htmlFor="crs-general-comments">
                    General Comments
                  </label>
                  <RichTextEditor
                    id="crs-general-comments"
                    placeholder="Any other comments about this call..."
                    value={feedbackData.generalComments}
                    onChange={(v) => handleFeedbackChange("generalComments", v)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="crs-footer">
            <span className="feedback-cancel-text" onClick={handleClose}>
              Cancel
            </span>

            <button
              onClick={handleSaveClick}
              disabled={isSaving}
              className="crs-save-btn"
            >
              {isSaving ? "Saving..." : "Save and Send"}
            </button>
          </div>
        </div>
      </div>

      {/* SAVE & SHARE CONFIRMATION */}
      {showShareModal && (
        <div
          className="crs-share-overlay"
          onClick={(e) => e.target === e.currentTarget && !isShareActionBusy && setShowShareModal(false)}
        >
          <div className="crs-share-modal" onClick={(e) => e.stopPropagation()}>
            <p className="crs-share-title">
              Would you like to share this feedback with the agent?
            </p>

            <div className="crs-share-actions">
              <button
                className="crs-share-btn crs-share-btn-primary"
                onClick={handleEmailToAgent}
                disabled={isShareActionBusy}
              >
                {isEmailing ? (
                  <>
                    <span className="crs-share-loader" /> Sending...
                  </>
                ) : (
                  "Email to Agent"
                )}
              </button>

              <button
                className="crs-share-btn crs-share-btn-secondary"
                onClick={handleDownloadPdf}
                disabled={isShareActionBusy}
              >
                {isDownloading ? (
                  <>
                    <span className="crs-share-loader crs-share-loader-dark" /> Generating...
                  </>
                ) : (
                  "Download PDF"
                )}
              </button>

              <button
                className="crs-share-btn crs-share-btn-ghost"
                onClick={handleNotNow}
                disabled={isShareActionBusy}
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EMAIL PREVIEW — exactly what the agent will receive.
          The body renders in a sandboxed iframe rather than being injected
          into the page: it is email HTML built server-side, and dropping it
          into the app's DOM would let its styles bleed into the portal and
          give it script access it has no business having. */}
      {emailPreview && (
        <div
          className="crs-share-overlay"
          onClick={(e) =>
            e.target === e.currentTarget && setEmailPreview(null)
          }
        >
          <div
            className="crs-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="crs-preview-head">
              <div>
                <h3 className="crs-preview-title">Email preview</h3>
                <p className="crs-preview-note">
                  This is the message that will be sent. Nothing has been sent
                  or saved yet.
                </p>
              </div>
              <button
                type="button"
                className="crs-preview-close"
                onClick={() => setEmailPreview(null)}
                aria-label="Close preview"
              >
                ×
              </button>
            </div>

            <div className="crs-preview-meta">
              <div>
                <span>To</span>
                {emailPreview.to?.length ? emailPreview.to.join(", ") : "—"}
              </div>
              {emailPreview.cc?.length ? (
                <div>
                  <span>Cc</span>
                  {emailPreview.cc.join(", ")}
                </div>
              ) : null}
              <div>
                <span>Subject</span>
                {emailPreview.subject || "—"}
              </div>
            </div>

            {/* The reviewer can correct their own wording before it goes, like
                composing in Outlook — but only their own wording. The call
                details, score table, headings and sign-off are facts about the
                review, not prose to be reworded, so they are locked. To, Cc and
                Subject are fixed for the same reason.

                Locked by default, opened selectively: the container is NOT
                editable, and the effect above grants editing only to the blocks
                the server marked. Doing it the other way round — editable
                container with islands switched off — leaves anything the server
                adds later editable by accident.

                A contentEditable div rather than an iframe: an iframe cannot be
                edited in place. The markup is our own server-built template,
                and the sanitiser has already removed anything executable, so it
                carries only inline styles on its own elements — which cannot
                leak into the surrounding page. */}
            <div
              ref={previewBodyRef}
              className="crs-preview-body"
              onInput={captureEditedBody}
              dangerouslySetInnerHTML={{ __html: emailPreview.html }}
            />

            <div className="crs-preview-actions">
              <span className="crs-preview-hint">
                {editedBody !== null
                  ? "Edited — your changes will be sent."
                  : "You can edit the highlighted feedback above; the rest is fixed."}
              </span>
              <button
                type="button"
                className="crs-share-btn crs-share-btn-ghost"
                onClick={() => {
                  setEmailPreview(null);
                  setEditedBody(null);
                }}
                disabled={isEmailing}
              >
                Cancel
              </button>
              <button
                type="button"
                className="crs-share-btn crs-share-btn-primary"
                onClick={handleConfirmSend}
                disabled={isEmailing}
              >
                {isEmailing ? (
                  <>
                    <span className="crs-share-loader" /> Sending...
                  </>
                ) : (
                  "Send to Agent"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DISCARD CHANGES CONFIRMATION */}
      {showDiscardModal && (
        <div className="crs-share-overlay">
          <div className="crs-share-modal" onClick={(e) => e.stopPropagation()}>
            <p className="crs-share-title">
              You have unsaved changes. Are you sure you want to discard them?
            </p>
            <div className="crs-share-actions">
              <button
                className="crs-share-btn crs-share-btn-primary"
                onClick={handleDiscardConfirm}
              >
                Discard Changes
              </button>
              <button
                className="crs-share-btn crs-share-btn-ghost"
                onClick={() => setShowDiscardModal(false)}
              >
                Keep Editing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default CallReviewSheetPopup;
